import { useState, useEffect } from "react";
import { Formik, Field, Form } from "formik";
import {
    InputGroup,
    Label,
    SubmitBtn,
    SideCancelBtn,
    Wrapper,
    ScrollableContent,
    ActionSection,
    ModalHeader,
} from "../Styles/InputStyles";
import { MdClose } from "react-icons/md";
import tw from "tailwind-styled-components";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import DropZone from "../Notice/Dropzone";
import {
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
} from "@mui/material";

/* ---------------- CONSTANTS ---------------- */

const STAFF_DOCUMENT_TYPES = {
    AADHAAR: 1,
    AADHAAR_BACK: 2,
    PAN: 3,
    SELFI: 4,
    POLICE_VERIFICATION: 5,
    OFFER_LETTER: 7,
    INCREMENT_LETTER: 8,
    CANCELLED_CHEQUE: 9,
    OTHERS: 50,
};

const DOCUMENT_TYPE_LABELS = {
    1: "Aadhaar Front",
    2: "Aadhaar Back",
    3: "PAN Card",
    4: "Selfie",
    5: "Police Verification",
    7: "Offer Letter",
    8: "Increment Letter",
    9: "Cancelled Cheque",
    50: "Others",
};

/* ---------------- VALIDATION ---------------- */

const documentSchema = Yup.object().shape({
    documentType: Yup.number()
        .required("Document type is required"),
    file: Yup.mixed().required("File is required"),
});

const StaffDocumentUploadModal = ({
    setIsDocumentAdd,
    uploadDocMutate,
    isDocLoading,
    staffId,
}) => {
    const initialDocumentValues = {
        documentType: 1,
        file: null,
    };

    const [height, setHeight] = useState(window.innerHeight);

    useEffect(() => {
        const handleResize = () => setHeight(window.innerHeight);
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleDocumentSubmit = (values) => {
        const formData = new FormData();
        formData.append("type", values.documentType);
        formData.append("files", values.file[0]);
        formData.append("staffId", staffId);

        uploadDocMutate(formData);
    };

    return (
        <Container style={{ height: `${height}px`, overflow: "auto" }}>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">Upload Document</h1>
                <button onClick={() => setIsDocumentAdd(false)}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialDocumentValues}
                    validationSchema={documentSchema}
                    onSubmit={handleDocumentSubmit}
                >
                    {({ values, setFieldValue, errors, touched }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                <Wrapper>

                                    {/* DOCUMENT TYPE DROPDOWN */}
                                    <InputGroup>
                                        <Label>Document Type *</Label>
                                        <FormControl
                                            fullWidth
                                            size="small"
                                            error={touched.documentType && Boolean(errors.documentType)}
                                        >
                                            <Select
                                                value={values.documentType}
                                                displayEmpty
                                                onChange={(e) =>
                                                    setFieldValue("documentType", e.target.value)
                                                }
                                            >
                                                <MenuItem value="" disabled>
                                                    Select document type
                                                </MenuItem>

                                                {Object.values(STAFF_DOCUMENT_TYPES).map((type) => (
                                                    <MenuItem key={type} value={type}>
                                                        {DOCUMENT_TYPE_LABELS[type]}
                                                    </MenuItem>
                                                ))}
                                            </Select>

                                            {touched.documentType && errors.documentType && (
                                                <FormHelperText>
                                                    {errors.documentType}
                                                </FormHelperText>
                                            )}
                                        </FormControl>
                                    </InputGroup>

                                    {/* FILE UPLOAD */}
                                    <InputGroup>
                                        <Label>Upload Document *</Label>
                                        <FieldWrapper>
                                            <Field name="file">
                                                {(props) => (
                                                    <DropZone
                                                        fields={props.field}
                                                        setFieldValue={props.form.setFieldValue}
                                                        componentFor="expense"
                                                        acceptedFiles="image/*,application/pdf"
                                                        File={
                                                            props.field.value
                                                                ? [props.field.value]
                                                                : null
                                                        }
                                                    />
                                                )}
                                            </Field>
                                        </FieldWrapper>
                                        {errors.file && touched.file && (
                                            <ErrorMessage>{errors.file}</ErrorMessage>
                                        )}
                                    </InputGroup>

                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection>
                                <SideCancelBtn
                                    type="button"
                                    onClick={() => setIsDocumentAdd(false)}
                                    disabled={isDocLoading}
                                >
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn type="submit" disabled={isDocLoading}>
                                    {isDocLoading ? "Uploading..." : "Upload"}
                                </SubmitBtn>
                            </ActionSection>
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

/* ---------------- STYLES ---------------- */

const Header = tw.div`flex items-center justify-between bg-[#0078BD] py-4 px-4`;
const Container = tw.div`bg-white w-full h-full min-h-[100vh] flex flex-col`;
const Title = tw.h1`text-lg md:text-xl font-medium text-white`;
const ErrorMessage = tw.div`text-red-500 text-xs mt-1`;
const FieldWrapper = tw.div`w-full`;

export default StaffDocumentUploadModal;